import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Search, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { clientApi, type Client } from "@/services/api";
import { getLatestBmrUpdate, needsDietReview } from "@/lib/dietReview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Clients = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; client: Client | null }>({ open: false, client: null });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    goal: "",
    image: "https://randomuser.me/api/portraits/lego/1.jpg", // Default image
    age: 30, // Default age
  });
  const navigate = useNavigate();
  
  // Fetch clients from API
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await clientApi.getAll();
        setClients(response);
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast({
          title: "Error",
          description: "Failed to load clients. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchClients();
  }, []);
  
  // Handle search with debounce
  useEffect(() => {
    if (searchQuery.trim() === "") {
      const fetchAllClients = async () => {
        try {
          const response = await clientApi.getAll();
          setClients(response);
        } catch (error) {
          console.error("Error fetching all clients:", error);
        }
      };
      
      fetchAllClients();
      return;
    }
    
    const debounceSearch = setTimeout(async () => {
      try {
        const response = await clientApi.search(searchQuery);
        setClients(response);
      } catch (error) {
        console.error("Error searching clients:", error);
      }
    }, 500);
    
    return () => clearTimeout(debounceSearch);
  }, [searchQuery]);

  const handleAddNewClient = () => {
    setShowNewClientDialog(true);
  };

  const openDeleteDialog = (client: Client, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeleteDialog({ open: true, client });
  };

  const handleDeleteClient = async () => {
    if (!deleteDialog.client?._id) return;
    try {
      await clientApi.delete(deleteDialog.client._id);
      setClients(prev => prev.filter(c => c._id !== deleteDialog.client!._id));
      setDeleteDialog({ open: false, client: null });
      toast({ title: "Client Deleted", description: "The client was removed successfully.", variant: "default" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete client. Please try again.", variant: "destructive" });
    }
  };

  const handleCreateClient = async () => {
    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.gender || !formData.age) {
        toast({
          title: "Required Fields",
          description: "Please fill in all required fields (name, email, gender, and age)",
          variant: "destructive",
        });
        return;
      }
      
      // Create client via API - with proper type casting
      const clientData = {
        ...formData,
        age: Number(formData.age),
        gender: formData.gender as "male" | "female" | "other",
        goal: formData.goal || "Not specified",
        image: formData.image || "https://randomuser.me/api/portraits/lego/1.jpg",
        phone: formData.phone || "",
        visitCount: {
          total: 0,
          thisMonth: 0,
          thisWeek: 0
        }
      };
      
      console.log('Creating client with data:', clientData);
      
      const newClient = await clientApi.create(clientData);
      console.log('Client created:', newClient);
      
      // Add the new client to the list
      setClients(prevClients => [...prevClients, newClient]);
      
      // Reset form data
      setFormData({
        name: "",
        email: "",
        phone: "",
        gender: "",
        goal: "",
        image: "https://randomuser.me/api/portraits/lego/1.jpg",
        age: 30,
      });
      
      // Close the dialog
      setShowNewClientDialog(false);
      
      toast({
        title: "Success",
        description: "New client has been added successfully.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error creating client:", error);
      toast({
        title: "Error",
        description: "Failed to create client. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, gender: value }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">
            Manage and track your client information
          </p>
        </div>
        <Button onClick={handleAddNewClient}>
          <Plus className="mr-2 h-4 w-4" /> Add New Client
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search clients..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center p-8 border rounded-lg">
          <p className="text-muted-foreground">No clients found. Add your first client to get started.</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => {
          const latestBmrUpdate = getLatestBmrUpdate(client.diet);
          const dietReviewOverdue = needsDietReview(latestBmrUpdate, client.dietReviewedAt);
          return (
          <Card 
              key={client._id} 
            className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/clients/${client._id}`)}
          >
            <CardContent className="p-0">
              <div className="flex items-start gap-4 p-4">
                <div className="relative h-16 w-16 rounded-full overflow-hidden">
                  <img 
                    src={client.image} 
                    alt={client.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    {client.name}
                    {dietReviewOverdue && (
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full bg-red-500 shrink-0"
                        title="BMR not reviewed in over 10 days"
                      />
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                  <p className="text-sm truncate">{client.goal}</p>
                </div>
                <div className="shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => openDeleteDialog(client, e)}
                    title="Delete client"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
      )}

      <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name*
              </Label>
              <Input
                id="name"
                className="col-span-3"
                placeholder="Enter client name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email*
              </Label>
              <Input
                id="email"
                type="email"
                className="col-span-3"
                placeholder="client@example.com"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="age" className="text-right">
                Age*
              </Label>
              <Input
                id="age"
                type="number"
                className="col-span-3"
                placeholder="Enter age"
                value={formData.age}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Phone
              </Label>
              <Input
                id="phone"
                className="col-span-3"
                placeholder="(123) 456-7890"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="gender" className="text-right">
                Gender*
              </Label>
              <Select value={formData.gender} onValueChange={handleSelectChange}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="goal" className="text-right">
                Goal
              </Label>
              <Input
                id="goal"
                className="col-span-3"
                placeholder="Client's primary goal"
                value={formData.goal}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewClientDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClient}>
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Client</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            Are you sure you want to delete "{deleteDialog.client?.name}"? This action cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, client: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteClient}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
